import { ChangeEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Tab,
  Tabs,
  Typography,
  Divider,
  makeStyles,
  createStyles,
  Checkbox,
  Grid,
} from '@material-ui/core';
import { Outlet, useBlocker, useLocation, useNavigate, useParams } from 'react-router-dom';
import TGBreadCrumbs from '../common/TGBreadCrumbs';
import {
  EVENT_CONFIGURATION_TABS,
  ROOT_RECORDS_ROUTE,
  getUrlFirstParam,
  goToWithReplace,
} from '../../../../../utils/Utils';
import { ConfigContext } from '../../../../../context/ConfigContext';
import { getAggregateIndex } from '../../../../../utils/navigationUtils';
import { errorMsg, successMsg } from '../../../../../components/SnackbarUtilsConfigurator';
import { useAggregateAPI } from '../../../../../context/fakeAPIHooks/useAggregateAPI';
import { JSONSchema6 } from 'json-schema';
import ConfigurationHeader from '../common/ConfigurationHeader';
import { useDataMapDiagram } from '../../../../../map-editor/src';
import { useSchemaLookup } from '../../../../../utils/useSchemaLookup';
import { convertV2FormTemplateToJsonSchema } from '../../../../../pages/aggregates/utils/V2FormTemplateToJsonSchema';
import { sample } from '../../../../../pages/aggregates/utils/sampleForm';
import { propertiesToSchema } from '../../../../../pages/aggregates/utils/PropertiesToSchemaConverter';
import useCommonStyles from 'views/useCommonStyles';
import AccordionCard from 'components/AccordionCard';
import { colors } from 'utils/colors';
import useRouteBlocker from 'common/useBlocker';
import { TextInputTG } from 'views/components/formElements';
import { isEqual } from 'lodash';
import { faCircleExclamation } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const EventConfiguration = () => {
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const tempRef = useRef({});

  const AggregateAPI = useAggregateAPI();
  const { config, getEvent, getEventVersions, getEventVersion, getEventSchema, getEventOptions } =
    useContext(ConfigContext);
  const { aggregate, eventName, eventVersion } = useParams() as {
    aggregate: string;
    eventName: string;
    eventVersion: string;
  };
  const aggrIndex = getAggregateIndex(config, aggregate);
  const [activeTab, setActiveTab] = useState<number>(0);
  const initialDoNotNotify =
    getEventOptions(aggrIndex, eventName, +eventVersion)?.doNotNotify || false;
  const [doNotNotify, setDoNotNotify] = useState<boolean>(initialDoNotNotify);
  const initialIsArchivable =
    getEventOptions(aggrIndex, eventName, +eventVersion)?.archivableOptions?.isArchivable || false;
  const [isArchivable, setIsArchivable] = useState<boolean>(initialIsArchivable);
  const initialKeepFirst =
    getEventOptions(aggrIndex, eventName, +eventVersion)?.archivableOptions?.keepFirst || 0;
  const [keepFirst, setKeepFirst] = useState<number>(initialKeepFirst);
  const initialIsKeepFirst = isArchivable && keepFirst > 0;
  const [isKeepFirst, setIsKeepFirst] = useState<boolean>(initialIsKeepFirst);
  const initialKeepLast =
    getEventOptions(aggrIndex, eventName, +eventVersion)?.archivableOptions?.keepLast || 1;
  const [keepLast, setKeepLast] = useState<number>(initialKeepLast);
  const initialIsKeepLast = isArchivable && keepLast > 1;
  const [isKeepLast, setIsKeepLast] = useState<boolean>(initialIsKeepLast);
  const initialKeepLastTime =
    getEventOptions(aggrIndex, eventName, +eventVersion)?.archivableOptions?.keepLastTime || 0;
  const [keepLastTime, setKeepLastTime] = useState<number>(initialKeepLastTime);
  const event = getEvent(aggrIndex, eventName);
  const inputActualSchemaRef = useRef<JSONSchema6>({});
  const eventSchemaVersion =
    getEventVersion(aggrIndex, eventName, +eventVersion)?.versionNumber || 1;

  useEffect(() => {
    setDoNotNotify(initialDoNotNotify);
    setIsArchivable(initialIsArchivable);
    setKeepFirst(initialKeepFirst);
    setKeepLast(initialKeepLast);
    setKeepLastTime(initialKeepLastTime);
    setIsKeepFirst(initialIsKeepFirst);
    setIsKeepLast(initialIsKeepLast);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventVersion, eventName, aggrIndex]);

  const localSchemas = useMemo(() => {
    tempRef.current = {};
    return {
      EVENT: {
        schema:
          getEventSchema(aggrIndex, eventName, +eventVersion) ||
          convertV2FormTemplateToJsonSchema(sample),
        schemaLabel: eventName,
      },
      STATE: {
        schema: propertiesToSchema(config.aggregates[aggrIndex].properties || {}),
        schemaLabel: 'Properties',
      },
    };
  }, [aggrIndex, eventName, eventVersion, config.aggregates, getEventSchema]);

  const schemaLookup = useSchemaLookup({
    currentAggregateType: aggregate,
    localSchemas: localSchemas,
  });
  const eventMapDiagramData = useDataMapDiagram({
    scenario: 'EVENT_AGGREGATE_MAP',
    schemaLookup: schemaLookup,
    dataMap:
      config.aggregates[aggrIndex].events?.[eventName]?.versions[+eventVersion]?.aggregateMap,
  });

  const handleChange = (event: ChangeEvent<{}>, newValue: number) => {
    goToWithReplace(
      navigate,
      `${ROOT_RECORDS_ROUTE}${aggregate}/events/${eventName}/version/${eventVersion}/${
        newValue === 0 ? 'eventSchema' : 'mapping'
      }`
    );
  };

  const updateActiveTab = useCallback(() => {
    const pathName = location.pathname;
    const route = getUrlFirstParam(
      pathName,
      `${ROOT_RECORDS_ROUTE}${aggregate}/events/${eventName}/version/${eventVersion}/`
    );
    setActiveTab(route === 'mapping' ? 1 : 0);
  }, [location, aggregate, eventName, eventVersion]);

  useEffect(() => {
    updateActiveTab();
  }, [updateActiveTab, location]);

  const handleRemoveVersion = async (
    eventName: string,
    versionIndex: number,
    versionNumber: number
  ) => {
    const versions = getEventVersions(aggrIndex, eventName);
    if (versions?.length === 1) {
      return errorMsg(
        'At least one version of the event have to remain. Consider deleting the entire event instead.'
      );
    }
    const { error } = await AggregateAPI.removeEventVersion(aggrIndex, eventName, versionIndex);
    if (error) return;
    successMsg(
      `Version "${versionNumber}" of the "${eventName}" event belonging to the "${aggregate}" has been successfully deleted.`
    );
  };

  const handleAddEventVersion = async (eventName: string, versionIndex: number) => {
    const { error } = await AggregateAPI.addEventVersion(aggrIndex, eventName, versionIndex);
    if (error) return;
    successMsg(`New version of the "${eventName}" event has been successfully created`);
  };

  const isDirty = () => {
    return (
      !isEqual(initialDoNotNotify, doNotNotify) ||
      !isEqual(initialIsArchivable, isArchivable) ||
      !isEqual(initialKeepFirst, keepFirst) ||
      !isEqual(initialKeepLast, keepLast) ||
      !isEqual(initialKeepLastTime, keepLastTime) ||
      !isEqual(initialIsKeepFirst, isKeepFirst) ||
      !isEqual(initialIsKeepLast, isKeepLast)
    );
  };

  const save = () => {
    if (isDirty()) {
      updateOptions();
    }
    if (activeTab === 0) {
      updateSchema();
    } else {
      updateMapping();
    }
    successMsg('The event has been saved');
  };

  const updateOptions = async () => {
    const { error } = await AggregateAPI.updateEventOptions(
      aggrIndex,
      eventName,
      +eventVersion,
      doNotNotify,
      isArchivable,
      isKeepFirst ? keepFirst : 0,
      isKeepLast ? keepLast : 1,
      keepLastTime
    );
    if (error) return;
  };

  const updateSchema = async () => {
    const { error } = await AggregateAPI.updateEventSchema(
      aggrIndex,
      eventName,
      +eventVersion,
      inputActualSchemaRef.current
    );
    if (error) return;
  };

  const onDiscard = async () => {
    eventMapDiagramData.resetMapDiagram();
  };

  const updateMapping = async () => {
    const eventMapDiagramModel = eventMapDiagramData.model?.getMapDefinition();
    if (eventMapDiagramModel) {
      const { error } = await AggregateAPI.updateAggregateMap(
        aggrIndex,
        eventName,
        +eventVersion,
        eventMapDiagramModel
      );
      if (error) return;
    }
  };

  const handleInputSchema = (schema: JSONSchema6) => {
    inputActualSchemaRef.current = schema;
  };

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      currentLocation.pathname !== nextLocation.pathname && isDirty()
  );
  useRouteBlocker({ blocker, onSave: save });

  return (
    <Box className={commonClasses.container}>
      <Box className={`${commonClasses.innerContainer} ${commonClasses.eventInnerContainer}`}>
        <>
          <Box className={[commonClasses.titleBarContainer, commonClasses.rootContainer].join(' ')}>
            <TGBreadCrumbs
              title="Events"
              backPath={`${ROOT_RECORDS_ROUTE}${aggregate}/events`}
              selectedItem={eventName}
            />
            <ConfigurationHeader
              type="event"
              name={eventName}
              versionNos={(event?.versions || []).map((version) => version.versionNumber)}
              versionChangePath={`${ROOT_RECORDS_ROUTE}${aggregate}/events/${eventName}/version/versionIndex/${
                activeTab === 1 ? 'mapping' : 'eventSchema'
              }`}
              selectedVersion={Number(eventVersion)}
              handleAddVersion={handleAddEventVersion}
              handleRemoveVersion={handleRemoveVersion}
              showImportButton={activeTab === 0}
              hideCancelButton={true}
              aggregate={aggregate}
              aggregateIndex={aggrIndex}
              schemaVersion={eventSchemaVersion}
              actualSchema={inputActualSchemaRef.current}
              save={save}
            />
          </Box>
        </>
        <Box className={classes.root}>
          <AccordionCard header="Options">
            <Grid container className={classes.optionContainer}>
              <Grid item xs={12} md={4} className={classes.options}>
                <Box className={classes.optionsContainer}>
                  <Checkbox
                    checked={doNotNotify}
                    color="primary"
                    onChange={() => {
                      setDoNotNotify(!doNotNotify);
                    }}
                  />
                  <Typography className={[classes.addContainer, classes.bodyText].join(' ')}>
                    Do Not Notify
                  </Typography>
                </Box>
                <Box className={classes.optionsContainer}>
                  <Checkbox
                    checked={isArchivable}
                    color="primary"
                    onChange={() => {
                      setIsArchivable(!isArchivable);
                    }}
                  />
                  <Typography className={[classes.addContainer, classes.bodyText].join(' ')}>
                    Is Archivable
                  </Typography>
                </Box>
              </Grid>
              {isArchivable && <></>}
            </Grid>
          </AccordionCard>
          {isArchivable && (
            <AccordionCard header="Archive">
              {isKeepLast && (
                <Box className={classes.tooltip}>
                  <FontAwesomeIcon icon={faCircleExclamation} className={classes.tooltip} />
                  <Typography className={classes.tip}>
                    When using both ‘Keep Last N Events’ and ‘Keep from Last N Days’ together,
                    events meeting either condition will be retained
                  </Typography>
                </Box>
              )}
              <Grid item xs={12} md={5} className={classes.options}>
                <Grid item xs={12} md={10} className={classes.options}>
                  <Box
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
                  >
                    <Box className={classes.inputOptionsContainer}>
                      <>
                        <Typography className={classes.bodyText}>Keep From Last Days</Typography>
                      </>
                      <TextInputTG
                        type="number"
                        id="keepLastTime"
                        onChange={(value) => setKeepLastTime(+value)}
                        value={keepLastTime}
                        containerStyle={classes.countInput}
                        InputProps={{
                          inputProps: {
                            min: 0,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={10} className={classes.options}>
                  <Box className={classes.optionsContainer}>
                    <Box className={classes.inputOptionsContainer}>
                      <Checkbox
                        checked={isKeepFirst}
                        color="primary"
                        onChange={() => {
                          setIsKeepFirst(!isKeepFirst);
                        }}
                      />
                      <>
                        <Typography className={classes.bodyText}>Keep First N Events</Typography>
                      </>
                      {isKeepFirst && (
                        <TextInputTG
                          type="number"
                          id="keepFirst"
                          onChange={(value) => setKeepFirst(+value)}
                          value={keepFirst}
                          containerStyle={classes.countInput}
                          InputProps={{
                            inputProps: {
                              min: 0,
                            },
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={10} className={classes.options}>
                  <Box className={classes.optionsContainer}>
                    <Box className={classes.inputOptionsContainer}>
                      <Checkbox
                        checked={isKeepLast}
                        color="primary"
                        onChange={() => {
                          setIsKeepLast(!isKeepLast);
                        }}
                      />
                      <>
                        <Typography className={classes.bodyText}>Keep Last N Events</Typography>
                      </>
                      {isKeepLast && (
                        <TextInputTG
                          type="number"
                          id="keepLast"
                          onChange={(value) => setKeepLast(+value)}
                          value={keepLast}
                          containerStyle={classes.countInput}
                          InputProps={{
                            inputProps: {
                              min: 1,
                            },
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </AccordionCard>
          )}
        </Box>
        <Box className={`${commonClasses.tabContainer} ${commonClasses.eventTabContainer}`}>
          <Tabs
            value={activeTab}
            onChange={handleChange}
            indicatorColor="primary"
            textColor="primary"
            aria-label="tabs"
          >
            {EVENT_CONFIGURATION_TABS.map((eventConfigurationTab) => (
              <Tab
                key={eventConfigurationTab}
                label={
                  <Typography className={commonClasses.label}>{eventConfigurationTab}</Typography>
                }
              />
            ))}
          </Tabs>
          <Divider />
          <Box className={`${commonClasses.panelContainer} ${commonClasses.eventPanel}`}>
            <Outlet
              context={{
                onChange: handleInputSchema,
                eventMapDiagramData,
                save,
                onDiscard,
                schemaRef: tempRef,
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EventConfiguration;

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      width: '100%',
      marginTop: 10,
    },
    addContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    bodyText: {
      '&.MuiTypography-body1': {
        fontSize: 15,
      },
      color: colors.black,
      fontStyle: 'normal',
      lineHeight: '100%',
      width: 272,
      zIndex: 1,
    },
    optionContainer: {
      width: '100%',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
    },
    options: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    optionsContainer: {
      display: 'flex',
      alignItems: 'center',
      zIndex: 1,
    },
    countInput: {
      '& .MuiInputBase-root': {
        width: 70,
        height: 30,
        padding: 0,
        '& input': {
          width: 70,
          height: 30,
          padding: '0px 5px 0px 10px',
        },
      },
      '& .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${colors.grayd9}`,
      },
    },
    inputOptionsContainer: {
      display: 'flex',
      alignItems: 'center',
      paddingTop: '7px',
      gap: '4px',
    },
    tooltipIcon: {
      fontSize: 14,
      color: colors.black,
    },
    tip: {
      fontSize: 14,
      paddingLeft: '4px',
    },
    tooltip: {
      marginBottom: '1px',
      display: 'flex',
      flex: 'row',
      alignItems: 'center',
    },
    row: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
  })
);
